'use client';

// v3 커밋4 — 현장 상세 데스크탑 우측 레일. ≥1024px 에서만 보인다 (.kd-detail-rail).
//
// 순서: ① 리드폼 진입 ② 카톡(부가) ③ 계산기.
// 현장 상세에서 1순위는 리드폼이다 — 오픈채팅 URL 은 파라미터를 못 받아
// '누가·어느 현장에서' 가 남는 경로가 폼뿐이다.
//
// ⚠️ ①은 폼 자체가 아니라 폼으로 가는 진입 카드다.
//    LeadForm 을 여기서 한 번 더 렌더하면 같은 페이지에 form 이 두 벌, id 도 두 벌이 된다
//    (id="lead-form" / input id="kd-lead-name"). getElementById 는 첫 번째만 잡아
//    하단 액션바·이 카드의 스크롤이 숨은 쪽을 가리키게 되고, 라벨-입력 연결도 깨진다.
//    리드 도달이 이 작업의 최대 리스크라 폼 인스턴스는 페이지당 한 벌로 유지한다.
//
// ── CPL v5 §A-5 · 「사이드·본문 2벌 → 1벌」 (2026-09-23) ──────────────────────
// ⛔ 여기 있던 ③ 같은 지역 현장 · ④ 바로가기를 **걷었다.**
//    본문 하단에 «같은 링크» 가 이미 있었다(NearbySitesSection · RegionHubLinks).
//    두 벌이 DOM 에 함께 실리고 CSS 로 한쪽씩 숨는 구조였다 —
//    레일은 <1024px 에서 display:none, 본문 쪽은 ≥1024px 에서 .kd-lg-hide.
//    화면에는 한 벌만 보이지만 **크롤러는 두 벌을 다 읽는다**. 같은 내부 링크가
//    한 페이지에서 두 번 나가고, 앵커 텍스트가 서로 달랐다
//    (레일 「{광역} 다른 현장」 vs 본문 「{시군구} 다른 현장」 — 실측).
// → 본문 쪽을 «정본» 으로 삼았다. 본문은 `.kd-lg-hide` 를 떼서 모든 폭에서 보인다.
//    레일을 정본으로 하면 <1024px 에서 그 링크가 통째로 사라진다(방문자 다수가 그쪽이다).
// ⚠️ ③ 계산기는 «본문에 대응물이 없어» 남긴다. 2벌이 아니다.
//
// ⚠️ 이 컴포넌트를 쓰는 곳은 `/apt/[id]/page.tsx` «한 곳» 이다(실측 2026-09-23).

import Link from 'next/link';
import { LEAD_FORM_ID } from '@/lib/apt/detail-anchors';
import { leadCopy } from '@/lib/apt/lead-copy';
import { trackLeadClick } from '@/lib/apt/lead-track';
import { useLeadView } from '@/components/apt/useLeadView';


export type SiteDetailRailProps = {
  siteSlug: string;
  siteName: string;
  /** ONESHOT §C-1: 단계별 문구 */
  lifecycleStage?: string | null;
  /** Q-8 — 정비사업 현장인가. 모르면 비워 둔다(정비판 문구). */
  isRedev?: boolean;
  redevInProgress?: boolean;
  showLeadForm: boolean;
  /**
   * hc3 L11 — true 면 종전 고정 문장(「잔여 세대·일정을 담당자가 직접 안내해 드립니다.」)을 단계와 무관하게 그대로 낸다.
   * rank_lock 보존 문장(rank-lock-sentences.json)에 이 문장이 든 현장에서 호출부가 켠다(교차 — page.tsx 는 레인 c).
   */
  keepLegacyLede?: boolean;
};

/** 종전 고정 문장. 분양 중(offering) 단계에서는 그대로 맞는 말이라 그 단계의 문구로 계속 쓴다.
 *  hc3 통합(HC-A-210·HC-E-004) — 문구 정본은 lib/apt/lead-copy 의 offering.rail(단계 미상 = offering) 한 벌. */
export const RAIL_LEGACY_LEDE = leadCopy(null).rail;

/**
 * hc3 L11 — 레일 카드 본문 한 줄. 「잔여 세대」는 분양 중 단계에서만 맞는 말이다
 * (조합설립·시공사선정 같은 공고 전 단계, 입주 끝난 단지에서는 대상이 틀린다).
 * hc3 통합 — 레일 한 줄 정본은 lead-copy 의 LeadCopy.rail(레인 a, 단계별) 하나다(offering.rail = 종전 문장).
 *   레인 e 의 종전 판(폼 lede 재사용)과 레인 a 의 rail 이 갈리지 않게 rail 로 통일했다.
 */
export function railLede(
  lifecycleStage: string | null | undefined,
  opts: { isRedev?: boolean; redevInProgress?: boolean; keepLegacyLede?: boolean } = {},
): string {
  if (opts.keepLegacyLede) return RAIL_LEGACY_LEDE;
  return leadCopy(lifecycleStage, '', { redev: opts.isRedev, redevInProgress: opts.redevInProgress }).rail;
}

const panel: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '14px',
  marginBottom: 12,
};

export default function SiteDetailRail({
  siteSlug, siteName, showLeadForm, lifecycleStage, isRedev, redevInProgress, keepLegacyLede,
}: SiteDetailRailProps) {
  /* P0-A′ — 레일 진입 카드의 «노출» 이 없었다. 실측 8/25~8/31: 노출 0 · 클릭 1.
     분모가 없는 슬롯은 클릭률을 낼 수 없고, 「클릭이 1뿐」이 자리가 나쁜 탓인지
     보이지 않은 탓인지도 못 가른다.
     ⚠️ showLeadForm 을 그대로 분모 조건으로 넘긴다 — 폼이 없는 현장까지 세면 분모가
        부푼다(SiteActionBar 의 hasForm 과 같은 판정). */
  const leadRef = useLeadView<HTMLDivElement>('rail', showLeadForm, {
    site_slug: siteSlug,
    lifecycle_stage: lifecycleStage,
  });

  return (
    <>
      {/* ① 리드폼 진입 */}
      {showLeadForm && (
        /* ⚠️ H6-6 — 배경을 «불투명» 으로 둔다. --brand-bg 는 rgba(...,0.08) 이라
           뒤에 무엇이 깔리느냐에 따라 글자가 읽히기도 안 읽히기도 했다(히어로 위에서
           실제로 안 읽혔다). 반투명 카드는 어떤 배경 위에 놓일지 보장할 수 없다.
           brand 색은 «테두리로만» 남긴다 — 정체성은 유지하고 가독성은 배경이 책임진다. */
        <div ref={leadRef} className="kd-rail-leadcard" style={{ ...panel, borderColor: 'var(--brand)', background: 'var(--bg-surface)' }}>
          <p style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-title)', color: 'var(--text-primary)', margin: '0 0 4px', lineHeight: 1.3, wordBreak: 'keep-all' }}>
            {siteName} {leadCopy(lifecycleStage, '', { redev: isRedev, redevInProgress }).band.replace(' · 무료', '')}
          </p>
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.5, wordBreak: 'keep-all' }}>
            {railLede(lifecycleStage, { isRedev, redevInProgress, keepLegacyLede })}
          </p>
          <a
            href={`#${LEAD_FORM_ID}`}
            onClick={() => trackLeadClick('rail', { site_slug: siteSlug, lifecycle_stage: lifecycleStage })}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 44, borderRadius: 'var(--radius-sm)',
              background: 'var(--brand)', color: 'var(--text-inverse)',
              fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-body)', textDecoration: 'none',
            }}
          >
            안내 신청하기
          </a>
        </div>
      )}

      {/* RD-2 [3] — ② 카톡 레일 링크 제거(상세 카톡 = SiteDock 1 + FAQ 1 ≤2). talk-banner 'rail' 슬롯 부착 지점만 걷었다. */}

      {/* ⛔ ③ 같은 지역 현장 · ④ 바로가기 — 파일 머리말 §A-5 참조. 본문이 정본이다. */}

      {/* v8-B2 · 계산기 — 분양 리드와 맞물리는 3종만.
           급여·군인·상속·쇼핑 계산기는 분양 상담과 무관해 현장 상세에 링크하지 않는다.
           ⚠️ 계산기는 네이버 검색 직접 유입이 30일 1,249건(69%)인 독립 유입원이다 —
              여기서 하는 것은 내부 링크 추가뿐이고 라우트·사이트맵은 건드리지 않는다. */}
      {/* NXDC D-2 — 「계산기」 섹션(h2)을 내리고 「관련 도구」 한 줄로. 링크 3개는 그대로(내부 링크 보존). */}
      <nav aria-label="관련 도구" style={panel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-body)', color: 'var(--text-tertiary)' }}>관련 도구</span>
          <Link href="/calc/real-estate" style={chip}>부동산 계산기</Link>
          <Link href="/calc/property-tax" style={chip}>세금 계산기</Link>
          <Link href="/calc/loan" style={chip}>대출 계산기</Link>
        </div>
      </nav>
    </>
  );
}

const chip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  padding: '0 12px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--bg-sunken)',
  color: 'var(--text-secondary)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 600,
  textDecoration: 'none',
};
